import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { SEO, getServiceSchema, getFAQSchema } from '../../components/SEO';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Baby, CheckCircle, ArrowRight, Phone, BookOpen, Star, Heart } from 'lucide-react';

export const ChildAdolescentPage = () => {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "At what age can a child start therapy?",
      answer: "I work with children as young as 5 years old through age 18. For younger children, I use play therapy and creative techniques. For older children and teens, I adjust my approach to include more talk therapy while still keeping sessions engaging and age-appropriate."
    },
    {
      question: "How do I know if my child needs therapy?",
      answer: "Consider therapy if your child is experiencing persistent behavioral changes, academic struggles, withdrawal from activities, difficulty managing emotions, sleep or appetite changes, or challenges with peers. If you're concerned about your child's well-being, a consultation can help determine if therapy would be beneficial."
    },
    {
      question: "Will I know what happens in my child's sessions?",
      answer: "Yes, parent involvement is important. I provide regular updates and progress reports while maintaining appropriate confidentiality for your child. For younger children, parents are more involved. For teens, we balance their need for privacy with keeping you informed about important matters. Safety concerns are always shared with parents."
    },
    {
      question: "What if my child doesn't want to talk in therapy?",
      answer: "That's completely normal, especially at first. I use various techniques beyond traditional talk therapy—including play, art, games, and activities—to help children express themselves comfortably. Building trust takes time, and I'm skilled at helping even reluctant children open up at their own pace."
    },
    {
      question: "How long will my child need therapy?",
      answer: "It varies based on the issues and goals. Some children benefit from short-term therapy (8-12 sessions) for specific issues, while others need longer-term support for more complex challenges. We'll regularly review progress and adjust the treatment plan as needed."
    },
    {
      question: "Do you offer telehealth for children and teens in Hawaii?",
      answer: "Yes! Telehealth can work very well for children and adolescents, especially teens who may feel more comfortable at home. For younger children, parent involvement helps ensure engagement. Telehealth is available throughout Hawaii and provides flexible scheduling for busy families."
    }
  ];

  const serviceSchema = getServiceSchema(
    "Child & Adolescent Counseling",
    "Professional child and teen therapy in Hawaii. Licensed counselor specializing in behavioral issues, anxiety, depression, and developmental challenges for ages 5-18.",
    "https://aloha-parents.preview.emergentagent.com/services/child-adolescent"
  );

  const faqSchema = getFAQSchema(faqs);

  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [serviceSchema, faqSchema]
  };

  const ageGroups = [
    {
      title: "Young Children (5-10)",
      issues: ["Behavioral challenges", "Anxiety and fears", "Social skills development", "Emotional regulation"],
      icon: Baby
    },
    {
      title: "Pre-Teens (11-13)",
      issues: ["School stress", "Peer pressure", "Self-esteem issues", "Family conflicts"],
      icon: BookOpen
    },
    {
      title: "Teenagers (14-18)",
      issues: ["Identity development", "Depression/anxiety", "Academic pressure", "Relationship issues"],
      icon: Star
    }
  ];

  const commonChallenges = [
    "Behavioral problems and defiance",
    "Anxiety and depression",
    "ADHD and focus difficulties",
    "Social skills and peer relationships",
    "School-related stress and performance",
    "Self-esteem and confidence issues",
    "Emotional regulation difficulties",
    "Trauma and adverse experiences",
    "Family conflict and communication",
    "Grief and loss",
    "Identity and self-discovery",
    "Technology and social media concerns"
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <SEO 
        title="Child & Adolescent Counseling in Hawaii"
        description="Expert child and teen therapy in Wailuku, HI. Licensed counselor helping children ages 5-18 with behavioral issues, anxiety, depression, and social challenges."
        keywords="child therapist Hawaii, teen counseling Maui, adolescent therapy Wailuku, child psychologist Hawaii, youth counseling"
        canonicalUrl="https://aloha-parents.preview.emergentagent.com/services/child-adolescent"
        schemaData={combinedSchema}
      />
      
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="flex items-center text-sm text-gray-600">
          <button onClick={() => navigate('/')} className="hover:text-teal-600 transition-colors">
            Home
          </button>
          <ArrowRight className="w-4 h-4 mx-2" />
          <button onClick={() => navigate('/#services')} className="hover:text-teal-600 transition-colors">
            Services
          </button>
          <ArrowRight className="w-4 h-4 mx-2" />
          <span className="text-gray-900 font-medium">Child & Adolescent Counseling</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-to-br from-amber-50 via-orange-50 to-white py-16 px-4 sm:px-6 lg:px-8 mb-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mb-6">
                <Baby className="w-8 h-8 text-amber-600" />
              </div>
              <Badge className="mb-4 bg-amber-100 text-amber-800">Specialized Youth Counseling</Badge>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Child & Adolescent Counseling in Hawaii
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Growing up comes with unique challenges. I provide specialized counseling for children 
                and teens, helping them navigate emotional, behavioral, and developmental challenges 
                with age-appropriate therapeutic approaches.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => navigate('/contact')}
                  className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-lg transition-all duration-200"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Schedule Consultation
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => window.location.href = 'tel:8084707650'}
                  className="border-2 border-amber-600 text-amber-600 hover:bg-amber-50 px-8 py-6 text-lg transition-all duration-200"
                >
                  Call (808) 470-7650
                </Button>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?w=800&q=80" 
                alt="Child in therapy"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Supporting Young People Through Life's Challenges
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Children and adolescents face unique developmental, emotional, and social challenges. 
                With over 11 years of experience in education and specialized training in child and 
                adolescent therapy, I understand the complexities of youth mental health and provide 
                age-appropriate support tailored to each child's needs.
              </p>
              <p>
                My approach combines evidence-based therapeutic techniques with creative, engaging 
                methods that resonate with young people. Whether your child is struggling with anxiety, 
                behavioral issues, social challenges, or developmental transitions, I create a safe, 
                supportive environment where they can express themselves and develop healthy coping skills.
              </p>
              <p>
                I work collaboratively with parents and families, recognizing that supporting a child 
                means supporting the entire family system. Together, we'll develop strategies that work 
                at home, school, and in social situations to help your child thrive.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Age Groups */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-800">Age-Appropriate Care</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Specialized Support for Every Age
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored therapeutic approaches for each developmental stage
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {ageGroups.map((group, index) => {
              const IconComponent = group.icon;
              return (
                <Card key={index} className="border-none shadow-lg bg-white hover:shadow-xl transition-all duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                      <IconComponent className="w-6 h-6 text-amber-600" />
                    </div>
                    <CardTitle className="text-2xl">{group.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {group.issues.map((issue, idx) => (
                        <li key={idx} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{issue}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Common Challenges */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-purple-100 text-purple-800">We Can Help With</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Common Challenges We Address
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commonChallenges.map((challenge, index) => (
              <Card key={index} className="border-none shadow-lg bg-gradient-to-br from-white to-amber-50 hover:shadow-xl transition-all duration-300">
                <CardContent className="pt-6">
                  <div className="flex items-start">
                    <CheckCircle className="w-6 h-6 text-amber-600 mr-3 mt-1 flex-shrink-0" />
                    <p className="text-gray-700 leading-relaxed">{challenge}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=800&q=80" 
                alt="Teen counseling"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
            <div>
              <Badge className="mb-4 bg-green-100 text-green-800">My Approach</Badge>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                How I Work With Young People
              </h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Age-Appropriate Techniques</h3>
                  <p className="text-gray-700">
                    I use play therapy, creative expression, and talk therapy tailored to each child's 
                    developmental level and communication style.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Evidence-Based Methods</h3>
                  <p className="text-gray-700">
                    Utilizing CBT, DBT skills, and behavioral interventions proven effective for children 
                    and adolescents.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Family Involvement</h3>
                  <p className="text-gray-700">
                    Regular parent consultations and family sessions to ensure consistent support and 
                    reinforcement at home.
                  </p>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Strength-Based Focus</h3>
                  <p className="text-gray-700">
                    Building on each child's unique strengths and abilities to foster confidence and 
                    resilience.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Parents */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-indigo-100 text-indigo-800">For Parents</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Parents Can Expect
            </h2>
          </div>

          <div className="space-y-6">
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <Heart className="w-6 h-6 text-amber-600 mr-3" />
                  Initial Parent Consultation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  We'll start with a parent-only session to discuss your concerns, your child's history, 
                  and treatment goals. This helps me understand your family's unique situation.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <Baby className="w-6 h-6 text-amber-600 mr-3" />
                  Individual Child Sessions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  Your child will have private sessions in a comfortable, engaging environment where 
                  they can express themselves freely and work on their challenges.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center">
                  <CheckCircle className="w-6 h-6 text-amber-600 mr-3" />
                  Regular Parent Updates
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700 leading-relaxed">
                  I'll provide regular feedback and collaborate with you on strategies to support your 
                  child's progress at home and school.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-blue-100 text-blue-800">Common Questions</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Child & Adolescent Counseling FAQs
            </h2>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem 
              value="item-1"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-amber-600 transition-colors">
                At what age can a child start therapy?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                I work with children as young as 5 years old through age 18. For younger children, I use play 
                therapy and creative techniques. For older children and teens, I adjust my approach to include 
                more talk therapy while still keeping sessions engaging and age-appropriate.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-2"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-amber-600 transition-colors">
                How do I know if my child needs therapy?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Consider therapy if your child is experiencing persistent behavioral changes, academic struggles, 
                withdrawal from activities, difficulty managing emotions, sleep or appetite changes, or challenges 
                with peers. If you're concerned about your child's well-being, a consultation can help determine 
                if therapy would be beneficial.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-3"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-amber-600 transition-colors">
                Will I know what happens in my child's sessions?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Yes, parent involvement is important. I provide regular updates and progress reports while 
                maintaining appropriate confidentiality for your child. For younger children, parents are more 
                involved. For teens, we balance their need for privacy with keeping you informed about important 
                matters. Safety concerns are always shared with parents.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-4"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-amber-600 transition-colors">
                What if my child doesn't want to talk in therapy?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                That's completely normal, especially at first. I use various techniques beyond traditional talk 
                therapy—including play, art, games, and activities—to help children express themselves comfortably. 
                Building trust takes time, and I'm skilled at helping even reluctant children open up at their 
                own pace.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-5"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-amber-600 transition-colors">
                How long will my child need therapy?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                It varies based on the issues and goals. Some children benefit from short-term therapy (8-12 
                sessions) for specific issues, while others need longer-term support for more complex challenges. 
                We'll regularly review progress and adjust the treatment plan as needed.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem 
              value="item-6"
              className="bg-gray-50 border-none shadow-md rounded-lg px-6"
            >
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-amber-600 transition-colors">
                Do you offer telehealth for children and teens in Hawaii?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 text-base leading-relaxed pt-2">
                Yes! Telehealth can work very well for children and adolescents, especially teens who may feel 
                more comfortable at home. For younger children, parent involvement helps ensure engagement. 
                Telehealth is available throughout Hawaii and provides flexible scheduling for busy families.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-amber-600 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Help Your Child Thrive
          </h2>
          <p className="text-xl mb-8 opacity-95">
            Give your child the support they need to overcome challenges and build a brighter future.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              onClick={() => navigate('/contact')}
              className="bg-white text-amber-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold transition-all duration-200 shadow-xl"
            >
              Schedule Consultation
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => navigate('/resources')}
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold transition-all duration-200"
            >
              Parenting Resources
            </Button>
          </div>
          <p className="mt-6 text-sm opacity-80">
            Ages 5-18 • Telehealth and in-person available • Parent involvement encouraged
          </p>
        </div>
      </section>
    </div>
  );
};
